/**
 * Column definitions for the lead table.
 *
 * The Company column is not in here — it is always shown, always first, and is
 * the only column that renders two lines, so it has no width entry and cannot
 * be switched off.
 */
import type { Lead, PipelineOutcome, PipelineStage } from '@/types/crm'
import { qualificationScore, TIER_STYLES } from '@/types/crm'
import { formatDate } from '@/shared/util/dates'
import type {
  Cell as SharedCell,
  ColumnFilters as SharedColumnFilters,
  ColumnMeta,
  SortDir as SharedSortDir,
} from '@/shared/components/DataTable'
import { scoreStyle } from './qualification'
import { STATE_OF, type ProState } from '@/features/dashboards/proCompanyState'

/**
 * Where a lead's Skida PRO company stands — 22 Oct 2026.
 *
 * The dashboard's own state when the lead is linked and the companies are
 * read; `unlinked` when it is not linked to any company; `unknown` while the
 * companies load or when the read failed. Worked out by the workspace and
 * passed in, so this file stays free of any database read.
 */
export type LeadProState = ProState | 'unlinked' | 'unknown'

const PRO_ORDER: Record<LeadProState, number> = {
  unlinked: -1,
  unknown: 0,
  abandoned: 1,
  signedIn: 2,
  registered: 3,
  trial: 4,
  signedUp: 5,
}

export function proStateLabel(s: LeadProState): string {
  return s === 'unlinked' ? 'Not linked' : s === 'unknown' ? 'Unknown' : STATE_OF[s].label
}

export type ColumnKey =
  | 'stage'
  | 'leadScore'
  | 'estAcv'
  | 'score'
  | 'owner'
  | 'country'
  | 'contacts'
  | 'proUsers'
  | 'proState'
  | 'appUsers'
  | 'nextTask'
  | 'created'
  | 'updated'
  | 'seatsExpected'
  | 'seatsBought'
  | 'seatsActive'

/** `width` is the column's `fr` share; the Company column is fixed at 1.8fr. */
export const COLUMN_META: Record<ColumnKey, ColumnMeta> = {
  stage: { label: 'Stage', width: 1.5 },
  /** Harvested 0–100 with its tier. Distinct from `score`, which a rep sets. */
  leadScore: { label: 'Auto score', width: 0.85 },
  /** teamSize × the seat price. The tiebreaker between equal scores. */
  estAcv: { label: 'Est. ACV', width: 0.8 },
  score: { label: 'Fit', width: 0.6 },
  owner: { label: 'Owner', width: 0.9 },
  country: { label: 'Country', width: 0.8 },
  contacts: { label: 'Contacts', width: 0.6 },
  /** Members on the linked Skida PRO company — people already using the product. */
  proUsers: { label: 'PRO users', width: 0.75 },
  /** The linked Skida PRO company's state, as the PRO dashboard counts it. */
  proState: { label: 'PRO state', width: 0.9 },
  /** Contacts we know hold a consumer skida.app account. */
  appUsers: { label: 'App users', width: 0.75 },
  nextTask: { label: 'Next task', width: 1.2 },
  created: { label: 'Created', width: 0.9 },
  updated: { label: 'Updated', width: 0.9 },
  /** What a rep expects to sell. The only seat figure that exists pre-sale. */
  seatsExpected: { label: 'Seats', width: 0.7 },
  seatsBought: { label: 'Seats bought', width: 0.8 },
  seatsActive: { label: 'Seats active', width: 0.8 },
}

export const COLUMN_KEYS = Object.keys(COLUMN_META) as ColumnKey[]

/** What a rep needs to triage; the rest are one click away in the picker. */
/**
 * What Step 0 needs, since that is where every lead now starts.
 *
 * Score and country say whether the company is worth working; PRO and app users
 * say whether somebody there is already using Skida, which is the single
 * strongest reason not to archive something that looks thin on paper.
 */
export const DEFAULT_COLUMNS: ColumnKey[] = [
  'stage',
  'leadScore',
  'estAcv',
  'country',
  'proUsers',
  'proState',
  'appUsers',
]

export type SortKey = ColumnKey | 'name'
export type SortDir = SharedSortDir

/**
 * Columns whose header carries a multi-select filter.
 *
 * Only the two columns with a small, closed set of values. Owner would qualify
 * on paper, but the search box already answers ownership better (`mine`,
 * `unassigned`), and a dropdown per column turns the header into a control panel.
 */
export const FILTERABLE_COLUMNS: ColumnKey[] = ['stage', 'country', 'proState']

/** Selected values per filterable column. An empty or absent list means "all". */
export type ColumnFilters = SharedColumnFilters<ColumnKey>

/** The value a filter compares against, for one lead and one column. */
export function filterValue(lead: Lead, key: ColumnKey, pro: LeadProState = 'unknown'): string {
  return key === 'stage' ? lead.stage : key === 'proState' ? pro : lead.country
}

export function passesFilters(lead: Lead, filters: ColumnFilters, pro: LeadProState = 'unknown'): boolean {
  return FILTERABLE_COLUMNS.every((key) => {
    const selected = filters[key]
    if (!selected || selected.length === 0) return true
    return selected.includes(filterValue(lead, key, pro))
  })
}

/** A rendered cell: either a coloured pill or a single line of muted text. */
export type Cell = SharedCell<ColumnKey>

export function buildCells(
  lead: Lead,
  columns: ColumnKey[],
  stage: PipelineStage | PipelineOutcome,
  pro: LeadProState = 'unknown',
): Cell[] {
  const score = qualificationScore(lead.qualification)
  const style = scoreStyle(score)
  const e = lead.enrichment

  return columns.map((key): Cell => {
    switch (key) {
      case 'stage':
        return { key, badge: true, text: stage.label, bg: stage.bg, color: stage.color }
      case 'leadScore': {
        // A dash, not a zero. Nobody has assessed this lead — that must not sort
        // below one that was assessed and found weak.
        if (e.leadScore === null || e.tier === null) return { key, badge: false, text: '—' }
        const t = TIER_STYLES[e.tier]
        return { key, badge: true, text: `${e.leadScore} ${e.tier}`, bg: t.bg, color: t.color }
      }
      case 'estAcv':
        return {
          key,
          badge: false,
          text: e.estAcvEur === null ? '—' : `€${e.estAcvEur.toLocaleString('nb-NO')}`,
        }
      case 'score':
        return { key, badge: true, text: `${score}/3`, bg: style.bg, color: style.color }
      case 'owner':
        return { key, badge: false, text: lead.owner || '—' }
      case 'country':
        return { key, badge: false, text: lead.country || '—' }
      case 'contacts':
        return { key, badge: false, text: String(lead.contacts.length) }
      case 'proUsers': {
        // Linked with nobody on the roster is a different fact from not linked
        // at all, so the two render differently rather than both showing 0.
        if (!lead.skidaProCompanyId) return { key, badge: false, text: '—' }
        const n = lead.productUserCount || lead.guides.length
        return {
          key,
          badge: true,
          text: n > 0 ? `PRO · ${n}` : 'PRO',
          bg: 'var(--color-teal-bg)',
          color: 'var(--color-teal-text)',
        }
      }
      case 'proState': {
        if (pro === 'unlinked' || pro === 'unknown') return { key, badge: false, text: '—' }
        const st = STATE_OF[pro]
        return { key, badge: true, text: st.label, bg: 'var(--color-surface-muted)', color: st.color }
      }
      case 'appUsers': {
        const n = lead.contacts.filter((c) => c.knownSkidaUser === true).length
        if (n === 0) return { key, badge: false, text: '—' }
        return {
          key,
          badge: true,
          text: String(n),
          bg: 'var(--color-success-bg)',
          color: 'var(--color-success-text)',
        }
      }
      case 'nextTask':
        return { key, badge: false, text: lead.nextTask?.text || '—' }
      case 'created':
        return { key, badge: false, text: lead.createdAt ? formatDate(lead.createdAt) : '—' }
      case 'updated':
        return { key, badge: false, text: lead.updatedAt ? formatDate(lead.updatedAt) : '—' }
      case 'seatsExpected':
        // An em-dash rather than 0, because nobody has said yet — the same
        // distinction the field itself carries. A column of noughts would read
        // as a book full of worthless deals.
        return {
          key,
          badge: false,
          text: lead.seatsExpected > 0 ? String(lead.seatsExpected) : '—',
        }
      case 'seatsBought':
        return { key, badge: false, text: String(lead.seatsBought) }
      case 'seatsActive':
        return { key, badge: false, text: String(lead.seatsActive) }
    }
  })
}

/**
 * Sort key for one lead.
 *
 * Stage sorts by pipeline position, not alphabetically — "Contacted" before
 * "Proposal" is the only ordering that means anything here. Everything else is
 * a number or a lowercased string, and numbers and strings never mix within a
 * column, so a plain `<`/`>` comparison is safe.
 */
export function sortValue(
  lead: Lead,
  sortBy: SortKey,
  stageOrder: string[],
  pro: LeadProState = 'unknown',
): string | number {
  switch (sortBy) {
    case 'stage':
      return stageOrder.indexOf(lead.stage)
    case 'leadScore':
      // Unscored sorts last ascending and last descending — an unknown is not a
      // zero, and it should never lead the list either way.
      return lead.enrichment.leadScore ?? -1
    case 'estAcv':
      return lead.enrichment.estAcvEur ?? -1
    case 'score':
      return qualificationScore(lead.qualification)
    case 'owner':
      return lead.owner.toLowerCase()
    case 'country':
      return lead.country.toLowerCase()
    case 'contacts':
      return lead.contacts.length
    case 'proUsers':
      return lead.skidaProCompanyId ? lead.productUserCount || lead.guides.length || 1 : -1
    case 'proState':
      return PRO_ORDER[pro]
    case 'appUsers':
      return lead.contacts.filter((c) => c.knownSkidaUser === true).length
    case 'nextTask':
      return lead.nextTask?.dueDate ?? ''
    case 'created':
      return lead.createdAt ?? ''
    case 'updated':
      return lead.updatedAt ?? ''
    case 'seatsExpected':
      // Unset sorts below zero so the leads nobody has sized collect at one
      // end rather than mixing in with real small deals.
      return lead.seatsExpected > 0 ? lead.seatsExpected : -1
    case 'seatsBought':
      return lead.seatsBought
    case 'seatsActive':
      return lead.seatsActive
    default:
      return lead.name.toLowerCase()
  }
}

export function sortLeads(
  leads: Lead[],
  sortBy: SortKey,
  dir: SortDir,
  stageOrder: string[],
  proOf: (lead: Lead) => LeadProState = () => 'unknown',
): Lead[] {
  const sign = dir === 'asc' ? 1 : -1
  return [...leads].sort((a, b) => {
    const av = sortValue(a, sortBy, stageOrder, proOf(a))
    const bv = sortValue(b, sortBy, stageOrder, proOf(b))
    if (av < bv) return -sign
    if (av > bv) return sign
    // Company name is the tiebreaker, so equal stages don't shuffle on re-render.
    return a.name.localeCompare(b.name)
  })
}
